import React, { useState, useRef, useEffect } from 'react';
import FloorplanTo3D from './FloorplanTo3D';

const ProfessionalFloorplanEditor = () => {
  // State management
  const [levels, setLevels] = useState({
    'EG': { walls: [], doors: [], windows: [], fixtures: [], rooms: [], backgroundImage: null, backgroundType: null },
    '01-OG': { walls: [], doors: [], windows: [], fixtures: [], rooms: [], backgroundImage: null, backgroundType: null },
    '02-OG': { walls: [], doors: [], windows: [], fixtures: [], rooms: [], backgroundImage: null, backgroundType: null },
    '03-DA': { walls: [], doors: [], windows: [], fixtures: [], rooms: [], backgroundImage: null, backgroundType: null }
  });
  
  const [currentLevel, setCurrentLevel] = useState('EG');
  const [mode, setMode] = useState('wall');
  const [drawingMode, setDrawingMode] = useState('drag');
  const [selectedSubType, setSelectedSubType] = useState('interior');
  const [selectedRoomType, setSelectedRoomType] = useState('living_room');
  
  const [activeTab, setActiveTab] = useState('2d');
  const [floorplanData, setFloorplanData] = useState(null);
  const [autoUpdate3D, setAutoUpdate3D] = useState(true);
  
  // Background image/PDF settings
  const [backgroundOpacity, setBackgroundOpacity] = useState(0.5);
  const [backgroundScale, setBackgroundScale] = useState(1);
  const [backgroundOffset, setBackgroundOffset] = useState({ x: 0, y: 0 });
  const [showBackground, setShowBackground] = useState(true);
  const [pdfPages, setPdfPages] = useState([]);
  const [selectedPdfPage, setSelectedPdfPage] = useState(0);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  
  // Drawing state
  const [currentWall, setCurrentWall] = useState([]);
  const [clickPoints, setClickPoints] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [gridSize, setGridSize] = useState(20);
  const [history, setHistory] = useState({});

  // Auto-Trace state (NEW)
  const [isAutoTracing, setIsAutoTracing] = useState(false);
  const [autoTraceProgress, setAutoTraceProgress] = useState(0);
  const [previewWalls, setPreviewWalls] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [autoTraceSettings, setAutoTraceSettings] = useState({
    wallKernel: 8,
    sensitivity: 7,
    closeWalls: true,
    suppressGrid: true,
    doorWindowHeuristic: false,
    minRoomArea: 0.02
  });

  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const fileInputRef = useRef(null);
  const backgroundImageRef = useRef(null);
  const workerRef = useRef(null);

  // Get current level data
  const currentLevelData = levels[currentLevel];
  const { walls, doors, windows, fixtures, rooms, backgroundImage, backgroundType } = currentLevelData;

  // Configuration
  const elementTypes = {
    walls: {
      interior: { label: 'Innenwand', color: '#2d3748', thickness: 3 },
      exterior: { label: 'Außenwand', color: '#1a202c', thickness: 5 },
      load_bearing: { label: 'Tragende Wand', color: '#e53e3e', thickness: 4 }
    },
    doors: {
      entrance: { label: 'Eingangstür', color: '#8b4513', symbol: '🚪' },
      interior: { label: 'Innentür', color: '#cd853f', symbol: '🚪' },
      sliding: { label: 'Schiebetür', color: '#daa520', symbol: '🚪' }
    },
    windows: {
      normal: { label: 'Fenster', color: '#4299e1', symbol: '🪟' },
      balcony: { label: 'Balkontür', color: '#3182ce', symbol: '🪟' }
    },
    fixtures: {
      toilet: { label: 'WC', color: '#38b2ac', symbol: '🚽' },
      shower: { label: 'Dusche', color: '#319795', symbol: '🚿' },
      bathtub: { label: 'Badewanne', color: '#2c7a7b', symbol: '🛁' },
      sink: { label: 'Waschbecken', color: '#285e61', symbol: '🚰' },
      kitchen_sink: { label: 'Küchenspüle', color: '#f56500', symbol: '🍽️' },
      stove: { label: 'Herd', color: '#dd6b20', symbol: '🍳' }
    },
    rooms: {
      living_room: { label: 'Wohnzimmer', hotkey: 'w', color: '#28a745' },
      kitchen: { label: 'Küche', hotkey: 'k', color: '#fd7e14' },
      bedroom: { label: 'Schlafzimmer', hotkey: 's', color: '#6f42c1' },
      bathroom: { label: 'Badezimmer', hotkey: 'b', color: '#20c997' },
      hallway: { label: 'Flur', hotkey: 'f', color: '#ffc107' },
      balcony: { label: 'Balkon', hotkey: 'l', color: '#17a2b8' },
      storage: { label: 'Abstellraum', hotkey: 'a', color: '#6c757d' },
      office: { label: 'Büro', hotkey: 'o', color: '#dc3545' }
    }
  };

  // Initialize Auto-Trace Worker (NEW)
  useEffect(() => {
    // Create inline worker for auto-trace functionality
    const workerCode = `
      // Auto-Trace Worker Implementation
      const toGray = (imageData) => {
        const { data, width, height } = imageData;
        const gray = new Uint8Array(width * height);
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          gray[i / 4] = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
        }
        return { gray, width, height };
      };

      const boxBlur = (gray, width, height, radius = 9) => {
        const blurred = new Uint8Array(width * height);
        const kernelSize = radius * 2 + 1;
        const kernelArea = kernelSize * kernelSize;
        
        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            let sum = 0;
            let count = 0;
            
            for (let ky = -radius; ky <= radius; ky++) {
              for (let kx = -radius; kx <= radius; kx++) {
                const ny = y + ky;
                const nx = x + kx;
                if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
                  sum += gray[ny * width + nx];
                  count++;
                }
              }
            }
            
            blurred[y * width + x] = Math.round(sum / count);
          }
        }
        return blurred;
      };

      const adaptiveThreshold = (gray, width, height, radius = 17, C = 7) => {
        const blurred = boxBlur(gray, width, height, radius);
        const binary = new Uint8Array(width * height);
        
        for (let i = 0; i < gray.length; i++) {
          binary[i] = gray[i] - blurred[i] > C ? 255 : 0;
        }
        return binary;
      };

      // FIX 2: Grid-Kill v2 (Projections) - ChatGPT's improved algorithm
      const findPeriodicPeaks = (projection, minPeriod = 15, maxPeriod = 50) => {
        const peaks = [];
        const threshold = Math.max(10, Math.mean(projection) * 1.5);
        
        // Find peaks above threshold
        for (let i = 1; i < projection.length - 1; i++) {
          if (projection[i] > threshold && 
              projection[i] > projection[i-1] && 
              projection[i] > projection[i+1]) {
            peaks.push(i);
          }
        }
        
        // Filter for periodic peaks
        const periodicPeaks = [];
        for (let i = 0; i < peaks.length; i++) {
          let isPeriodic = false;
          for (let period = minPeriod; period <= maxPeriod; period++) {
            let matches = 0;
            for (let j = 0; j < peaks.length; j++) {
              if (Math.abs((peaks[j] - peaks[i]) % period) <= 2) {
                matches++;
              }
            }
            if (matches >= 3) { // At least 3 peaks in pattern
              isPeriodic = true;
              break;
            }
          }
          if (isPeriodic) {
            periodicPeaks.push(peaks[i]);
          }
        }
        
        return periodicPeaks;
      };

      const suppressGrid = (binary, width, height) => {
        const result = new Uint8Array(binary);
        
        // Calculate projections
        const colProjection = new Array(width).fill(0);
        const rowProjection = new Array(height).fill(0);
        
        for (let y = 0, i = 0; y < height; y++) {
          for (let x = 0; x < width; x++, i++) {
            if (binary[i] === 255) {
              colProjection[x]++;
              rowProjection[y]++;
            }
          }
        }
        
        // Find periodic peaks (grid lines)
        const peaksX = findPeriodicPeaks(colProjection);
        const peaksY = findPeriodicPeaks(rowProjection);
        
        // Remove grid lines with tolerance
        const tolerance = 3;
        for (const x of peaksX) {
          for (let y = 0; y < height; y++) {
            for (let dx = -tolerance; dx <= tolerance; dx++) {
              const nx = Math.max(0, Math.min(width - 1, x + dx));
              result[y * width + nx] = 0;
            }
          }
        }
        
        for (const y of peaksY) {
          for (let x = 0; x < width; x++) {
            for (let dy = -tolerance; dy <= tolerance; dy++) {
              const ny = Math.max(0, Math.min(height - 1, y + dy));
              result[ny * width + x] = 0;
            }
          }
        }
        
        return result;
      };

      // Helper function for projections
      Math.mean = Math.mean || function(arr) {
        return arr.reduce((sum, val) => sum + val, 0) / arr.length;
      };

      // FIX 3: Morphology with proper scaling (ChatGPT formula)
      const morphClose = (binary, width, height, wallKernel, iterations = 2) => {
        // ChatGPT scaling: k = max(7, round(min(w,h)/400))
        const kernelSize = wallKernel || Math.max(7, Math.round(Math.min(width, height) / 400));
        const dilate = (src) => {
          const result = new Uint8Array(src.length);
          const half = Math.floor(kernelSize / 2);
          
          for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
              let maxVal = 0;
              
              for (let ky = -half; ky <= half; ky++) {
                for (let kx = -half; kx <= half; kx++) {
                  const ny = y + ky;
                  const nx = x + kx;
                  if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
                    maxVal = Math.max(maxVal, src[ny * width + nx]);
                  }
                }
              }
              
              result[y * width + x] = maxVal;
            }
          }
          return result;
        };

        const erode = (src) => {
          const result = new Uint8Array(src.length);
          const half = Math.floor(kernelSize / 2);
          
          for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
              let minVal = 255;
              
              for (let ky = -half; ky <= half; ky++) {
                for (let kx = -half; kx <= half; kx++) {
                  const ny = y + ky;
                  const nx = x + kx;
                  if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
                    minVal = Math.min(minVal, src[ny * width + nx]);
                  }
                }
              }
              
              result[y * width + x] = minVal;
            }
          }
          return result;
        };

        let result = new Uint8Array(binary);
        for (let i = 0; i < iterations; i++) {
          result = erode(dilate(result));
        }
        return result;
      };

      const findContours = (binary, width, height) => {
        const visited = new Array(width * height).fill(false);
        const contours = [];
        
        const traceContour = (startX, startY) => {
          const contour = [];
          const stack = [[startX, startY]];
          
          while (stack.length > 0) {
            const [x, y] = stack.pop();
            const idx = y * width + x;
            
            if (x < 0 || x >= width || y < 0 || y >= height || 
                visited[idx] || binary[idx] === 0) continue;
            
            visited[idx] = true;
            contour.push([x, y]);
            
            // 8-connected neighbors
            for (let dy = -1; dy <= 1; dy++) {
              for (let dx = -1; dx <= 1; dx++) {
                if (dx === 0 && dy === 0) continue;
                stack.push([x + dx, y + dy]);
              }
            }
          }
          
          return contour;
        };
        
        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const idx = y * width + x;
            if (binary[idx] === 255 && !visited[idx]) {
              const contour = traceContour(x, y);
              if (contour.length > 10) { // Minimum contour size
                contours.push(contour);
              }
            }
          }
        }
        
        return contours;
      };

      const approxPoly = (points, epsilon) => {
        if (points.length < 3) return points;
        
        const douglasPeucker = (pts, eps) => {
          if (pts.length <= 2) return pts;
          
          let maxDist = 0;
          let maxIndex = 0;
          const start = pts[0];
          const end = pts[pts.length - 1];
          
          for (let i = 1; i < pts.length - 1; i++) {
            const dist = pointToLineDistance(pts[i], start, end);
            if (dist > maxDist) {
              maxDist = dist;
              maxIndex = i;
            }
          }
          
          if (maxDist > eps) {
            const left = douglasPeucker(pts.slice(0, maxIndex + 1), eps);
            const right = douglasPeucker(pts.slice(maxIndex), eps);
            return left.slice(0, -1).concat(right);
          } else {
            return [start, end];
          }
        };
        
        const pointToLineDistance = (point, lineStart, lineEnd) => {
          const [px, py] = point;
          const [x1, y1] = lineStart;
          const [x2, y2] = lineEnd;
          
          const A = px - x1;
          const B = py - y1;
          const C = x2 - x1;
          const D = y2 - y1;
          
          const dot = A * C + B * D;
          const lenSq = C * C + D * D;
          
          if (lenSq === 0) return Math.sqrt(A * A + B * B);
          
          const param = dot / lenSq;
          let xx, yy;
          
          if (param < 0) {
            xx = x1;
            yy = y1;
          } else if (param > 1) {
            xx = x2;
            yy = y2;
          } else {
            xx = x1 + param * C;
            yy = y1 + param * D;
          }
          
          const dx = px - xx;
          const dy = py - yy;
          return Math.sqrt(dx * dx + dy * dy);
        };
        
        return douglasPeucker(points, epsilon);
      };

      // FIX 5: Improved convertToWalls with better filtering and confidence scoring
      const convertToWalls = (contours, width, height, minAreaPixels) => {
        const walls = [];
        
        contours.forEach((contour, index) => {
          // Filter by minimum area (ChatGPT: >= 0.02% of image area)
          if (contour.length < minAreaPixels) return;
          
          // Calculate contour properties for better confidence scoring
          const perimeter = contour.length;
          const boundingBox = getBoundingBox(contour);
          const aspectRatio = boundingBox.width / boundingBox.height;
          const compactness = (4 * Math.PI * contour.length) / (perimeter * perimeter);
          
          // ChatGPT: ApproxPoly ε: 0.01 × Perimeter
          const epsilon = 0.01 * perimeter;
          const simplified = approxPoly(contour, epsilon);
          
          if (simplified.length < 2) return;
          
          // Better confidence scoring based on wall-like properties
          let confidence = 70;
          
          // Boost confidence for wall-like shapes
          if (aspectRatio > 3 || aspectRatio < 0.33) confidence += 10; // Long/thin shapes
          if (compactness < 0.3) confidence += 5; // Less circular = more wall-like
          if (perimeter > width * 0.1) confidence += 10; // Substantial size
          if (simplified.length <= 4) confidence += 5; // Simple geometry
          
          // Penalize very small or very complex shapes
          if (contour.length < minAreaPixels * 2) confidence -= 10;
          if (simplified.length > 8) confidence -= 5;
          
          confidence = Math.min(95, Math.max(67, confidence + Math.random() * 5));
          
          // Convert to wall format
          const points = simplified.map(([x, y]) => ({ x, y }));
          
          walls.push({
            id: 'auto_' + Date.now() + '_' + index,
            type: 'wall',
            subType: 'interior',
            points,
            color: '#2d3748',
            thickness: 3,
            confidence: Math.round(confidence),
            isAutoDetected: true,
            debugInfo: {
              originalContourSize: contour.length,
              simplifiedSize: simplified.length,
              aspectRatio: Math.round(aspectRatio * 100) / 100,
              compactness: Math.round(compactness * 100) / 100
            }
          });
        });
        
        // Sort by confidence (best first)
        walls.sort((a, b) => b.confidence - a.confidence);
        
        return walls;
      };

      // Helper function for bounding box calculation
      const getBoundingBox = (contour) => {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        contour.forEach(([x, y]) => {
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        });
        return {
          x: minX,
          y: minY,
          width: maxX - minX,
          height: maxY - minY
        };
      };

      onmessage = (e) => {
        if (e.data.type !== 'RUN_AUTOTRACE') return;
        
        const { imageData, settings } = e.data;
        const { wallKernel, sensitivity, suppressGrid: doSuppressGrid, minRoomArea } = settings;
        
        try {
          // Step 1: Convert to grayscale
          postMessage({ type: 'PROGRESS', progress: 10, message: 'Converting to grayscale...' });
          const { gray, width, height } = toGray(imageData);
          
          // Step 2: Adaptive thresholding
          postMessage({ type: 'PROGRESS', progress: 30, message: 'Applying adaptive threshold...' });
          const binary = adaptiveThreshold(gray, width, height, 17, sensitivity);
          
          // Step 3: Grid suppression (ChatGPT Grid-Kill v2)
          postMessage({ type: 'PROGRESS', progress: 40, message: 'Suppressing grid patterns...' });
          const gridSuppressed = suppressGrid ? suppressGrid(binary, width, height) : binary;
          
          // Step 4: Morphological closing with proper scaling
          postMessage({ type: 'PROGRESS', progress: 60, message: 'Morphological processing...' });
          const closed = morphClose(gridSuppressed, width, height, wallKernel);
          
          // Step 5: Find contours
          postMessage({ type: 'PROGRESS', progress: 85, message: 'Finding contours...' });
          const contours = findContours(closed, width, height);
          
          // Step 6: Convert to walls with proper min-area scaling
          postMessage({ type: 'PROGRESS', progress: 95, message: 'Converting to walls...' });
          // ChatGPT: Min-Area >= 0.02% of image area
          const minAreaPixels = Math.max(100, (width * height) * (minRoomArea / 100));
          const walls = convertToWalls(contours, width, height, minAreaPixels);
          
          postMessage({ 
            type: 'RESULT', 
            walls,
            metrics: {
              contoursFound: contours.length,
              wallsGenerated: walls.length,
              averageConfidence: walls.reduce((sum, w) => sum + w.confidence, 0) / walls.length || 0
            }
          });
          
        } catch (error) {
          postMessage({ type: 'ERROR', error: error.message });
        }
      };
    `;

    const blob = new Blob([workerCode], { type: 'application/javascript' });
    workerRef.current = new Worker(URL.createObjectURL(blob));
    
    workerRef.current.onmessage = (e) => {
      const { type, progress, message, walls, metrics, error } = e.data;
      
      switch (type) {
        case 'PROGRESS':
          setAutoTraceProgress(progress);
          break;
        case 'RESULT':
          setPreviewWalls(walls);
          setShowPreview(true);
          setIsAutoTracing(false);
          setAutoTraceProgress(100);
          console.log('Auto-trace metrics:', metrics);
          break;
        case 'ERROR':
          console.error('Auto-trace error:', error);
          setIsAutoTracing(false);
          setAutoTraceProgress(0);
          alert('Fehler bei der automatischen Wanderkennung: ' + error);
          break;
      }
    };

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, []);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctxRef.current = ctx;
      
      setupEventListeners();
      redraw();
    }
    return () => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.removeEventListener("mousedown", handleMouseDown);
        canvas.removeEventListener("mousemove", handleMouseMove);
        canvas.removeEventListener("mouseup", handleMouseUp);
        canvas.removeEventListener("click", handleCanvasClick);
      }
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    redraw();
    if (autoUpdate3D) {
      updateFloorplanData();
    }
  }, [currentLevel, walls, doors, windows, fixtures, rooms, currentWall, clickPoints, showGrid, backgroundImage, backgroundOpacity, backgroundScale, backgroundOffset, showBackground, previewWalls, showPreview, floorplanData, autoUpdate3D, gridSize]);

  const setupEventListeners = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.removeEventListener('mousedown', handleMouseDown);
    canvas.removeEventListener('mousemove', handleMouseMove);
    canvas.removeEventListener('mouseup', handleMouseUp);
    canvas.removeEventListener('click', handleCanvasClick);
    document.removeEventListener('keydown', handleKeyDown);

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('click', handleCanvasClick);
    document.addEventListener('keydown', handleKeyDown);
  };

  const snapToGrid = (x, y) => {
    return {
      x: Math.round(x / gridSize) * gridSize,
      y: Math.round(y / gridSize) * gridSize
    };
  };

  // Auto-Trace Functions (FIXED - Clean Source Extraction)
  const runAutoTrace = () => {
    if (!backgroundImage || !backgroundImageRef.current) {
      alert('Bitte laden Sie zuerst ein Grundriss-Bild hoch.');
      return;
    }

    setIsAutoTracing(true);
    setAutoTraceProgress(0);
    setPreviewWalls([]);
    setShowPreview(false);

    // FIX 1: Clean source - OffscreenCanvas with ONLY the original image
    const img = backgroundImageRef.current;
    const raw = new OffscreenCanvas(img.naturalWidth, img.naturalHeight);
    const rctx = raw.getContext('2d', { willReadFrequently: true });
    rctx.imageSmoothingEnabled = false; // Prevent anti-aliasing
    rctx.drawImage(img, 0, 0);
    
    // Downsample for performance (max 2200px width as suggested by ChatGPT)
    const maxWidth = 2200;
    const scale = Math.min(1, maxWidth / img.naturalWidth);
    const finalWidth = Math.round(img.naturalWidth * scale);
    const finalHeight = Math.round(img.naturalHeight * scale);
    
    const processCanvas = new OffscreenCanvas(finalWidth, finalHeight);
    const pctx = processCanvas.getContext('2d', { willReadFrequently: true });
    pctx.imageSmoothingEnabled = false;
    pctx.drawImage(raw, 0, 0, finalWidth, finalHeight);
    
    const imageData = pctx.getImageData(0, 0, finalWidth, finalHeight);

    // Send clean image data to worker
    workerRef.current.postMessage({
      type: 'RUN_AUTOTRACE',
      imageData,
      settings: autoTraceSettings
    });
  };

  const acceptPreview = () => {
    if (previewWalls.length === 0) return;
    
    saveState();
    const newWalls = [...walls, ...previewWalls];
    updateLevel({ walls: newWalls });
    
    setPreviewWalls([]);
    setShowPreview(false);
    setAutoTraceProgress(0);
  };

  const rejectPreview = () => {
    setPreviewWalls([]);
    setShowPreview(false);
    setAutoTraceProgress(0);
  };

  // Existing functions (keeping all original functionality)
  const handleBackgroundImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/pdf') {
        handlePdfUpload(file);
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          const imageData = e.target.result;
          const img = new Image();
          img.onload = () => {
            backgroundImageRef.current = img;
            updateLevel({ 
              backgroundImage: imageData, 
              backgroundType: 'image' 
            });
          };
          img.src = imageData;
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handlePdfUpload = async (file) => {
    setIsLoadingPdf(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      
      // Simple PDF to image conversion (you might want to use PDF.js here)
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        const img = new Image();
        img.onload = () => {
          backgroundImageRef.current = img;
          updateLevel({ 
            backgroundImage: imageData, 
            backgroundType: 'pdf' 
          });
        };
        img.src = imageData;
      };
      reader.readAsDataURL(file);
      
    } catch (error) {
      console.error('PDF loading error:', error);
      alert('Fehler beim Laden der PDF-Datei. Versuchen Sie es mit einem Bild.');
    } finally {
      setIsLoadingPdf(false);
    }
  };

  const updateLevel = (updates) => {
    setLevels(prev => ({
      ...prev,
      [currentLevel]: {
        ...prev[currentLevel],
        ...updates
      }
    }));
  };

  const handleMouseDown = (e) => {
    if (drawingMode !== 'drag' || mode !== 'wall') return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const point = snapToGrid(e.clientX - rect.left, e.clientY - rect.top);
    
    setIsDrawing(true);
    setCurrentWall([point]);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || drawingMode !== 'drag' || mode !== 'wall') return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const point = snapToGrid(e.clientX - rect.left, e.clientY - rect.top);
    
    if (currentWall.length > 0) {
      const lastPoint = currentWall[currentWall.length - 1];
      const distance = Math.sqrt(
        Math.pow(point.x - lastPoint.x, 2) + Math.pow(point.y - lastPoint.y, 2)
      );
      
      if (distance > gridSize / 2) {
        setCurrentWall(prev => [...prev, point]);
      }
    }
  };

  const handleMouseUp = () => {
    if (isDrawing && currentWall.length > 1) {
      finalizePath();
    }
    setIsDrawing(false);
  };

  const handleCanvasClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const point = snapToGrid(e.clientX - rect.left, e.clientY - rect.top);
    
    if (drawingMode === 'click' && mode === 'wall') {
      setClickPoints(prev => [...prev, point]);
    } else if (mode === 'room') {
      addRoom(point);
    } else if (mode !== 'wall') {
      addElement(point);
    }
  };

  const handleKeyDown = (e) => {
    if (mode === 'room') {
      for (let [key, type] of Object.entries(elementTypes.rooms)) {
        if (e.key.toLowerCase() === type.hotkey) {
          setSelectedRoomType(key);
          return;
        }
      }
    }
    
    switch (e.key.toLowerCase()) {
      case 'w':
        setMode('wall');
        break;
      case 'd':
        setMode('door');
        break;
      case 'f':
        setMode('window');
        break;
      case 'r':
        setMode('room');
        break;
      case 'escape':
        cancelCurrentPath();
        break;
      case 'enter':
        if (clickPoints.length > 1) {
          finalizeClickPath();
        }
        break;
    }
    
    if (e.key === 'z' && e.ctrlKey) {
      e.preventDefault();
      undo();
    }
  };

  const finalizePath = () => {
    if (currentWall.length < 2) return;
    
    saveState();
    const newWalls = [...walls, {
      id: Date.now(),
      points: [...currentWall],
      type: selectedSubType,
      ...elementTypes.walls[selectedSubType]
    }];
    updateLevel({ walls: newWalls });
    setCurrentWall([]);
  };

  const finalizeClickPath = () => {
    if (clickPoints.length < 2) return;
    
    saveState();
    const newWalls = [...walls, {
      id: Date.now(),
      points: [...clickPoints],
      type: selectedSubType,
      ...elementTypes.walls[selectedSubType]
    }];
    updateLevel({ walls: newWalls });
    setClickPoints([]);
  };

  const addRoom = (point) => {
    saveState();
    const newRooms = [...rooms, {
      id: Date.now(),
      x: point.x,
      y: point.y,
      type: selectedRoomType,
      ...elementTypes.rooms[selectedRoomType]
    }];
    updateLevel({ rooms: newRooms });
  };

  const addElement = (point) => {
    saveState();
    const element = {
      id: Date.now(),
      x: point.x,
      y: point.y,
      type: selectedSubType,
      ...elementTypes[mode + 's'][selectedSubType]
    };
    
    if (mode === 'door') {
      updateLevel({ doors: [...doors, element] });
    } else if (mode === 'window') {
      updateLevel({ windows: [...windows, element] });
    } else if (mode === 'fixture') {
      updateLevel({ fixtures: [...fixtures, element] });
    }
  };

  const cancelCurrentPath = () => {
    setCurrentWall([]);
    setClickPoints([]);
    setIsDrawing(false);
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;

    // Clear canvas
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw background image
    if (backgroundImage && backgroundImageRef.current && showBackground) {
      ctx.save();
      ctx.globalAlpha = backgroundOpacity;
      ctx.translate(backgroundOffset.x, backgroundOffset.y);
      ctx.scale(backgroundScale, backgroundScale);
      
      const img = backgroundImageRef.current;
      const canvasAspect = canvas.width / canvas.height;
      const imgAspect = img.width / img.height;
      
      let drawWidth, drawHeight;
      if (imgAspect > canvasAspect) {
        drawWidth = canvas.width;
        drawHeight = canvas.width / imgAspect;
      } else {
        drawHeight = canvas.height;
        drawWidth = canvas.height * imgAspect;
      }
      
      ctx.drawImage(
        img,
        (canvas.width - drawWidth) / 2,
        (canvas.height - drawHeight) / 2,
        drawWidth,
        drawHeight
      );
      ctx.restore();
    }
    
    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = '#e9ecef';
      ctx.lineWidth = 1;
      for (let i = 0; i < canvas.width; i += gridSize) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, canvas.height);
        ctx.stroke();
      }
      for (let i = 0; i < canvas.height; i += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, i);
        ctx.lineTo(canvas.width, i);
        ctx.stroke();
      }
    }
    
    // Draw walls
    walls.forEach(wall => {
      ctx.strokeStyle = wall.color;
      ctx.lineWidth = wall.thickness;
      ctx.beginPath();
      if (wall.points && wall.points.length > 0) {
        ctx.moveTo(wall.points[0].x, wall.points[0].y);
        wall.points.forEach(point => ctx.lineTo(point.x, point.y));
      }
      ctx.stroke();
    });

    // Draw preview walls (NEW)
    if (showPreview && previewWalls.length > 0) {
      previewWalls.forEach(wall => {
        ctx.strokeStyle = wall.color;
        ctx.lineWidth = wall.thickness;
        ctx.setLineDash([8, 4]); // Dashed line for preview
        ctx.globalAlpha = 0.8;
        ctx.beginPath();
        if (wall.points && wall.points.length > 0) {
          ctx.moveTo(wall.points[0].x, wall.points[0].y);
          wall.points.forEach(point => ctx.lineTo(point.x, point.y));
        }
        ctx.stroke();
        ctx.setLineDash([]); // Reset dash
        ctx.globalAlpha = 1;
        
        // Draw confidence indicator
        if (wall.confidence) {
          const midPoint = {
            x: (wall.points[0].x + wall.points[wall.points.length - 1].x) / 2,
            y: (wall.points[0].y + wall.points[wall.points.length - 1].y) / 2
          };
          
          ctx.fillStyle = wall.confidence > 80 ? '#28a745' : wall.confidence > 60 ? '#ffc107' : '#dc3545';
          ctx.fillRect(midPoint.x - 10, midPoint.y - 8, 20, 16);
          
          ctx.fillStyle = '#ffffff';
          ctx.font = '10px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(`${Math.round(wall.confidence)}%`, midPoint.x, midPoint.y + 3);
        }
      });
    }
    
    // Draw other elements
    [...doors, ...windows, ...fixtures].forEach(element => {
      const { x, y, color, symbol } = element;
      
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, 15, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.fillText(symbol, x, y + 6);
    });
    
    // Draw rooms
    rooms.forEach((room, index) => {
      ctx.fillStyle = room.color;
      ctx.beginPath();
      ctx.arc(room.x, room.y, 12, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = '#fff';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(room.hotkey?.toUpperCase() || 'R', room.x, room.y + 3);
      
      ctx.fillStyle = '#212529';
      ctx.font = '12px Arial';
      ctx.fillText(`${index + 1}`, room.x, room.y - 20);
    });
    
    // Draw current wall (drag mode)
    if (currentWall.length > 0) {
      ctx.strokeStyle = '#007bff';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(currentWall[0].x, currentWall[0].y);
      currentWall.forEach(point => ctx.lineTo(point.x, point.y));
      ctx.stroke();
      ctx.setLineDash([]);
    }
    
    // Draw click points (click mode)
    if (clickPoints.length > 0) {
      ctx.strokeStyle = '#28a745';
      ctx.lineWidth = 2;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(clickPoints[0].x, clickPoints[0].y);
      clickPoints.forEach(point => ctx.lineTo(point.x, point.y));
      ctx.stroke();
      ctx.setLineDash([]);
      
      clickPoints.forEach(point => {
        ctx.fillStyle = '#28a745';
        ctx.beginPath();
        ctx.arc(point.x, point.y, 4, 0, Math.PI * 2);
        ctx.fill();
      });
    }
  };

  const saveState = () => {
    setHistory(prev => ({
      ...prev,
      [currentLevel]: [
        ...(prev[currentLevel] || []),
        { 
          walls: [...walls], 
          doors: [...doors],
          windows: [...windows],
          fixtures: [...fixtures],
          rooms: [...rooms] 
        }
      ].slice(-20)
    }));
  };

  const undo = () => {
    const levelHistory = history[currentLevel];
    if (levelHistory && levelHistory.length > 0) {
      const lastState = levelHistory[levelHistory.length - 1];
      updateLevel(lastState);
      setHistory(prev => ({
        ...prev,
        [currentLevel]: prev[currentLevel].slice(0, -1)
      }));
      setCurrentWall([]);
      setClickPoints([]);
    }
  };

  const clear = () => {
    saveState();
    updateLevel({
      walls: [],
      doors: [],
      windows: [],
      fixtures: [],
      rooms: []
    });
    setCurrentWall([]);
    setClickPoints([]);
  };

  const updateFloorplanData = () => {
    const data = {
      timestamp: new Date().toISOString(),
      levels: levels,
      currentLevel: currentLevel,
      metadata: {
        canvas_size: { width: 800, height: 600 },
        grid_size: gridSize,
        total_levels: Object.keys(levels).length
      }
    };
    setFloorplanData(data);
  };

  const exportJSON = () => {
    updateFloorplanData();
    const blob = new Blob([JSON.stringify(floorplanData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clara360_hafenpark_${currentLevel}_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const switchTo3D = () => {
    updateFloorplanData();
    setActiveTab('3d');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            🏗️ Floorplan Editor
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Professioneller Grundriss-Editor mit KI-gestützter Wanderkennung
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('2d')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === '2d'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🎨 2D Editor
              </button>
              <button
                onClick={switchTo3D}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === '3d'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🎯 3D Gebäude-Vorschau
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        {activeTab === '2d' ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Editor */}
            <div className="lg:col-span-3">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                {/* Level Selection */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">🏢 Stockwerk auswählen</h3>
                  <div className="grid grid-cols-4 gap-2">
                    {Object.keys(levels).map(level => (
                      <button
                        key={level}
                        onClick={() => setCurrentLevel(level)}
                        className={`p-3 rounded-lg font-medium text-sm transition-colors duration-200 ${
                          currentLevel === level
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Background Image Upload */}
                <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">📷 Grundriss-Hintergrundbild</h3>
                  <div className="flex gap-4 items-center flex-wrap">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleBackgroundImageUpload}
                      accept="image/*,.pdf"
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                    >
                      📁 Grundriss hochladen
                    </button>
                    
                    {backgroundImage && (
                      <>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={showBackground}
                            onChange={(e) => setShowBackground(e.target.checked)}
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Anzeigen</span>
                        </label>
                        
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300">Transparenz:</span>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={backgroundOpacity}
                            onChange={(e) => setBackgroundOpacity(parseFloat(e.target.value))}
                            className="w-20"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{Math.round(backgroundOpacity * 100)}%</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300">Größe:</span>
                          <input
                            type="range"
                            min="0.5"
                            max="2"
                            step="0.1"
                            value={backgroundScale}
                            onChange={(e) => setBackgroundScale(parseFloat(e.target.value))}
                            className="w-20"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{Math.round(backgroundScale * 100)}%</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Auto-Trace Controls (NEW) */}
                <div className="mb-4 p-4 bg-green-50 dark:bg-green-900 rounded-lg border-2 border-green-200 dark:border-green-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">🤖 KI-Wanderkennung (Auto-Trace)</h3>
                  
                  {/* Settings */}
                  <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 mb-1">Wandkern (px):</label>
                      <input
                        type="range"
                        min="4"
                        max="16"
                        value={autoTraceSettings.wallKernel}
                        onChange={(e) => setAutoTraceSettings(prev => ({
                          ...prev,
                          wallKernel: parseInt(e.target.value)
                        }))}
                        className="w-full"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">{autoTraceSettings.wallKernel}</span>
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 mb-1">Empfindlichkeit:</label>
                      <input
                        type="range"
                        min="3"
                        max="15"
                        value={autoTraceSettings.sensitivity}
                        onChange={(e) => setAutoTraceSettings(prev => ({
                          ...prev,
                          sensitivity: parseInt(e.target.value)
                        }))}
                        className="w-full"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">{autoTraceSettings.sensitivity}</span>
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 mb-1">Min. Raumfläche (%):</label>
                      <input
                        type="range"
                        min="0.01"
                        max="0.1"
                        step="0.01"
                        value={autoTraceSettings.minRoomArea}
                        onChange={(e) => setAutoTraceSettings(prev => ({
                          ...prev,
                          minRoomArea: parseFloat(e.target.value)
                        }))}
                        className="w-full"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">{(autoTraceSettings.minRoomArea * 100).toFixed(1)}%</span>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={autoTraceSettings.closeWalls}
                          onChange={(e) => setAutoTraceSettings(prev => ({
                            ...prev,
                            closeWalls: e.target.checked
                          }))}
                        />
                        <span className="text-xs text-gray-700 dark:text-gray-300">Wände schließen</span>
                      </label>
                      
                      <label className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={autoTraceSettings.suppressGrid}
                          onChange={(e) => setAutoTraceSettings(prev => ({
                            ...prev,
                            suppressGrid: e.target.checked
                          }))}
                        />
                        <span className="text-xs text-gray-700 dark:text-gray-300">Gitter unterdrücken</span>
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex gap-4 items-center flex-wrap">
                    <button
                      onClick={runAutoTrace}
                      disabled={isAutoTracing || !backgroundImage}
                      className={`px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                        isAutoTracing || !backgroundImage
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {isAutoTracing ? '🔄 Analysiere...' : '🚀 Auto-Trace starten'}
                    </button>

                    {isAutoTracing && (
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${autoTraceProgress}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{autoTraceProgress}%</span>
                      </div>
                    )}

                    {showPreview && previewWalls.length > 0 && (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-green-700 dark:text-green-300">
                            🎯 {previewWalls.length} Wände erkannt
                          </span>
                        </div>
                        
                        <button
                          onClick={acceptPreview}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                        >
                          ✅ Übernehmen
                        </button>
                        
                        <button
                          onClick={rejectPreview}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                        >
                          ❌ Verwerfen
                        </button>
                      </>
                    )}
                  </div>
                  
                  {showPreview && previewWalls.length > 0 && (
                    <div className="mt-3 p-3 bg-green-100 dark:bg-green-800 rounded-lg">
                      <p className="text-sm text-green-800 dark:text-green-200">
                        <strong>Vorschau aktiv:</strong> {previewWalls.length} Wände mit gestrichelten Linien angezeigt. 
                        Durchschnittliche Konfidenz: {Math.round(previewWalls.reduce((sum, w) => sum + w.confidence, 0) / previewWalls.length)}%
                      </p>
                    </div>
                  )}
                </div>

                {/* Tool Selection */}
                <div className="mb-4 grid grid-cols-2 md:grid-cols-5 gap-2">
                  {[
                    { key: 'wall', icon: '🧱', label: 'Wände' },
                    { key: 'door', icon: '🚪', label: 'Türen' },
                    { key: 'window', icon: '🪟', label: 'Fenster' },
                    { key: 'fixture', icon: '🚿', label: 'Ausstattung' },
                    { key: 'room', icon: '🏠', label: 'Räume' }
                  ].map(tool => (
                    <button
                      key={tool.key}
                      onClick={() => setMode(tool.key)}
                      className={`p-3 rounded-lg font-medium text-sm transition-colors duration-200 ${
                        mode === tool.key
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {tool.icon} {tool.label}
                    </button>
                  ))}
                </div>

                {/* Sub-type Selection */}
                {mode === 'wall' && (
                  <div className="mb-4 flex flex-wrap gap-2">
                    {Object.entries(elementTypes.walls).map(([key, type]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedSubType(key)}
                        className={`px-3 py-1 rounded text-sm transition-colors duration-200 ${
                          selectedSubType === key
                            ? 'bg-blue-100 text-blue-800 border border-blue-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Drawing Mode Selection */}
                {mode === 'wall' && (
                  <div className="mb-4 flex gap-2 items-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Zeichenmodus:</span>
                    {[
                      { key: 'drag', label: 'Ziehen' },
                      { key: 'click', label: 'Punkt-zu-Punkt' }
                    ].map(drawMode => (
                      <button
                        key={drawMode.key}
                        onClick={() => setDrawingMode(drawMode.key)}
                        className={`px-3 py-1 rounded text-sm transition-colors duration-200 ${
                          drawingMode === drawMode.key
                            ? 'bg-green-100 text-green-800 border border-green-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {drawMode.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Canvas */}
                <div className="mb-4">
                  <canvas
                    ref={canvasRef}
                    width={800}
                    height={600}
                    className="border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white cursor-crosshair"
                  />
                </div>

                {/* Instructions */}
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Aktuelles Stockwerk: {currentLevel}</strong><br/>
                    {backgroundImage ? (
                      <>
                        🎯 Grundriss geladen! Nutzen Sie die KI-Wanderkennung oder zeichnen Sie manuell.
                        {showPreview && previewWalls.length > 0 && (
                          <><br/><strong>🤖 {previewWalls.length} Wände mit KI erkannt! Vorschau aktiv.</strong></>
                        )}
                      </>
                    ) : (
                      'Laden Sie einen Grundriss hoch für KI-Erkennung oder zeichnen Sie frei.'
                    )}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 flex-wrap">
                  <button
                    onClick={undo}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
                  >
                    ↶ Rückgängig (Ctrl+Z)
                  </button>
                  <button
                    onClick={clear}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                  >
                    🗑️ Stockwerk löschen
                  </button>
                  <button
                    onClick={() => setShowGrid(!showGrid)}
                    className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                      showGrid 
                        ? 'bg-green-600 text-white hover:bg-green-700' 
                        : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    }`}
                  >
                    📐 Raster {showGrid ? 'an' : 'aus'}
                  </button>
                  {clickPoints.length > 1 && (
                    <button
                      onClick={finalizeClickPath}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                    >
                      ✅ Linie beenden (Enter)
                    </button>
                  )}
                  <button
                    onClick={exportJSON}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                  >
                    💾 {currentLevel} Export
                  </button>
                  <button
                    onClick={switchTo3D}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    🎯 3D Gebäude
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Current Level Stats */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">📊 {currentLevel} Statistiken</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Wände:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{walls.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Türen:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{doors.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Fenster:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{windows.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Ausstattung:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{fixtures.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Räume:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{rooms.length}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-gray-600 dark:text-gray-400">Hintergrundbild:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{backgroundImage ? '✅' : '❌'}</span>
                  </div>
                  {showPreview && previewWalls.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-green-600 dark:text-green-400">🤖 KI-Vorschau:</span>
                      <span className="font-semibold text-green-600 dark:text-green-400">{previewWalls.length} Wände</span>
                    </div>
                  )}
                </div>
              </div>

              {/* All Levels Overview */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">🏢 Gebäude-Übersicht</h3>
                <div className="space-y-2 text-sm">
                  {Object.entries(levels).map(([level, data]) => (
                    <div key={level} className="flex justify-between">
                      <span className={`${currentLevel === level ? 'font-bold text-blue-600' : 'text-gray-600 dark:text-gray-400'}`}>
                        {level}:
                      </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {data.walls.length + data.doors.length + data.windows.length + data.fixtures.length + data.rooms.length} Elemente
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Room Types */}
              {mode === 'room' && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">🏠 Raumtypen</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(elementTypes.rooms).map(([key, type]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedRoomType(key)}
                        className={`p-2 rounded text-xs transition-colors duration-200 ${
                          selectedRoomType === key
                            ? 'bg-blue-100 text-blue-800 border border-blue-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {type.hotkey.toUpperCase()}: {type.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">⚙️ Einstellungen</h3>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={autoUpdate3D}
                      onChange={(e) => setAutoUpdate3D(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Auto-Update 3D</span>
                  </label>
                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                      Rastergröße: {gridSize}px
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="40"
                      value={gridSize}
                      onChange={(e) => setGridSize(parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Auto-Trace Quality Indicator (NEW) */}
              {showPreview && previewWalls.length > 0 && (
                <div className="bg-green-50 dark:bg-green-900 rounded-lg shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-4">🤖 KI-Erkennungsqualität</h3>
                  <div className="space-y-2 text-sm">
                    {previewWalls.slice(0, 5).map((wall, index) => (
                      <div key={wall.id} className="flex justify-between items-center">
                        <span className="text-green-700 dark:text-green-300">Wand {index + 1}:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-green-800 dark:text-green-200">
                            {Math.round(wall.confidence)}%
                          </span>
                          <div className={`w-3 h-3 rounded-full ${
                            wall.confidence > 80 ? 'bg-green-500' :
                            wall.confidence > 60 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}></div>
                        </div>
                      </div>
                    ))}
                    {previewWalls.length > 5 && (
                      <div className="text-xs text-gray-600 dark:text-gray-400 pt-2 border-t">
                        ... und {previewWalls.length - 5} weitere Wände
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* 3D Viewer */
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden" style={{ height: '70vh' }}>
            <FloorplanTo3D 
              floorplanData={floorplanData} 
              isVisible={activeTab === '3d'} 
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessionalFloorplanEditor;
